import api from './api';
import { readList, readNumber } from './listResponse';

const withStore = (storeId, config = {}) => ({ ...config, _storeId: storeId });

export const normalizeExpense = (expense) => ({
  id: expense?.id ?? expense?.Id,
  category: expense?.category ?? expense?.Category,
  description: expense?.description ?? expense?.Description ?? '',
  amount: Number(expense?.amount ?? expense?.Amount) || 0,
  vatAmount: readNumber(expense?.vatAmount ?? expense?.VatAmount),
  expenseDate: expense?.expenseDate ?? expense?.ExpenseDate,
  paymentMethod: expense?.paymentMethod ?? expense?.PaymentMethod,
  supplierId: expense?.supplierId ?? expense?.SupplierId ?? null,
  supplierName: expense?.supplierName ?? expense?.SupplierName ?? '',
  reference: expense?.reference ?? expense?.Reference ?? '',
  notes: expense?.notes ?? expense?.Notes ?? '',
  sourceType: expense?.sourceType ?? expense?.SourceType ?? 'Manual',
  stockPurchaseId: expense?.stockPurchaseId ?? expense?.StockPurchaseId ?? null,
  seriesId: expense?.seriesId ?? expense?.SeriesId ?? null,
  seriesSequence: expense?.seriesSequence ?? expense?.SeriesSequence ?? null,
  isOverridden: Boolean(expense?.isOverridden ?? expense?.IsOverridden),
  status: expense?.status ?? expense?.Status ?? 'Active',
  isScheduled: Boolean(expense?.isScheduled ?? expense?.IsScheduled)
    || (expense?.status ?? expense?.Status) === 'Scheduled',
});

const categoryAmount = (row, posted) => Number(
  posted
    ? (row?.postedAmount ?? row?.PostedAmount)
    : (row?.scheduledAmount ?? row?.ScheduledAmount)
) || 0;

export const normalizeExpenseSummary = (data) => {
  const categoryRows = Array.isArray(data)
    ? data
    : (data?.byCategory ?? data?.categories ?? data?.ByCategory ?? []);
  const postedFromCategories = categoryRows.reduce((sum, row) => sum + categoryAmount(row, true), 0);
  const scheduledFromCategories = categoryRows.reduce((sum, row) => sum + categoryAmount(row, false), 0);
  const postedTotal = Array.isArray(data)
    ? postedFromCategories
    : (Number(data?.postedTotal ?? data?.totalAmount ?? data?.expenses ?? data?.moneyOut) || postedFromCategories);
  const scheduledTotal = Array.isArray(data)
    ? scheduledFromCategories
    : (Number(data?.scheduledTotal ?? data?.scheduled ?? data?.scheduledRestOfMonth) || scheduledFromCategories);
  const cashInRaw = Array.isArray(data) ? null : (data?.cashIn ?? data?.moneyIn ?? data?.totalReceived);
  const cashIn = cashInRaw == null || cashInRaw === '' ? null : Number(cashInRaw);

  return {
    postedTotal,
    scheduledTotal,
    cashIn: Number.isFinite(cashIn) ? cashIn : null,
    net: Number.isFinite(cashIn) ? Math.round((cashIn - postedTotal) * 100) / 100 : null,
    byCategory: categoryRows,
  };
};

const buildExpenseBody = (expense) => {
  const recurring = expense.recurring && expense.recurrence
    ? {
      frequency: expense.recurrence.frequency,
      intervalCount: Number(expense.recurrence.intervalCount) || 1,
      endMode: expense.recurrence.endMode,
      occurrenceCount: expense.recurrence.endMode === 'AfterCount'
        ? Number(expense.recurrence.occurrenceCount)
        : null,
      endDate: expense.recurrence.endMode === 'OnDate' ? expense.recurrence.endDate : null,
    }
    : null;

  return {
    category: expense.category,
    description: String(expense.description || '').trim(),
    amount: Number(expense.amount),
    vatAmount: expense.vatAmount === '' || expense.vatAmount == null ? null : Number(expense.vatAmount),
    expenseDate: expense.expenseDate,
    paymentMethod: expense.paymentMethod,
    supplierId: expense.supplierId || null,
    reference: expense.reference?.trim() || null,
    notes: expense.notes?.trim() || null,
    idempotencyKey: expense.idempotencyKey || null,
    recurring,
  };
};

const ExpenseService = {
  getExpenses: async (storeId, { from, to, page = 1, pageSize = 50 } = {}) => {
    const response = await api.get('/expenses', withStore(storeId, {
      params: { from, to, page, pageSize },
    }));
    const pageData = readList(response.data);
    return {
      ...pageData,
      items: pageData.items.map(normalizeExpense),
    };
  },

  getSummary: async (storeId, { from, to } = {}) => {
    const response = await api.get('/expenses/summary', withStore(storeId, {
      params: { from, to },
    }));
    return normalizeExpenseSummary(response.data);
  },

  getSeries: async (storeId, seriesId) => {
    const response = await api.get(`/expenses/series/${seriesId}`, withStore(storeId));
    const data = response.data || {};
    return {
      id: data.id ?? data.Id ?? seriesId,
      category: data.category ?? data.Category,
      description: data.description ?? data.Description ?? '',
      amount: Number(data.amount ?? data.Amount) || 0,
      vatAmount: readNumber(data.vatAmount ?? data.VatAmount),
      paymentMethod: data.paymentMethod ?? data.PaymentMethod,
      supplierId: data.supplierId ?? data.SupplierId ?? null,
      frequency: data.frequency ?? data.Frequency,
      intervalCount: Number(data.intervalCount ?? data.IntervalCount) || 1,
      startDate: data.startDate ?? data.StartDate,
      endMode: data.endMode ?? data.EndMode,
      occurrenceCount: data.occurrenceCount ?? data.OccurrenceCount ?? '',
      endDate: data.endDate ?? data.EndDate ?? '',
      status: data.status ?? data.Status,
    };
  },

  createExpense: async (storeId, expense) => {
    const response = await api.post('/expenses', buildExpenseBody(expense), withStore(storeId));
    return normalizeExpense(response.data);
  },

  updateExpense: async (storeId, expenseId, expense, scope) => {
    const response = await api.put(`/expenses/${expenseId}`, buildExpenseBody(expense), withStore(storeId, {
      params: scope ? { scope } : undefined,
    }));
    return normalizeExpense(response.data);
  },

  voidExpense: async (storeId, expenseId, { scope, voidReason } = {}) => {
    await api.post(`/expenses/${expenseId}/void`, { voidReason: voidReason || null }, withStore(storeId, {
      params: scope ? { scope } : undefined,
    }));
  },
};

export default ExpenseService;
